import { useId, useRef, useState, type ReactNode } from 'react';
import HelpOutlineIcon from '@mui/icons-material/HelpOutlineOutlined';
import { Box, Chip, IconButton, Popover, Tooltip, Typography } from '@mui/material';
import { useCopy } from '@/hooks/useCopy';

export interface HelpPopoverProps {
  /** Accessible name — the setting's label. */
  label: string;
  /** Short scope line, rendered first and visually distinct. */
  scope?: string;
  /** The long-form explanation, one paragraph. */
  body?: string;
  /** One line: the trigger's tooltip (hover and keyboard focus) and the popover's first line. */
  short?: string;
  /** Rich content (paragraphs, lists, a small table) under the short line. */
  detail?: ReactNode;
}

export function HelpPopover({ label, scope, body, short, detail }: HelpPopoverProps) {
  const { t } = useCopy();
  const popoverId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const open = Boolean(anchorEl);

  const close = () => {
    setAnchorEl(null);
    triggerRef.current?.focus();
  };

  const trigger = (
    <IconButton
      ref={triggerRef}
      type="button"
      size="small"
      aria-label={t('help.ariaLabel', 'Help for {{label}}', { label })}
      {...(open ? { 'aria-describedby': popoverId } : {})}
      onClick={(event) => setAnchorEl(event.currentTarget)}
      // The tooltip opens on any focus, not only `:focus-visible`: the short line is the
      // keyboard user's first answer (A11Y-001).
      onFocus={() => setTooltipOpen(true)}
      onBlur={() => setTooltipOpen(false)}
    >
      <HelpOutlineIcon fontSize="small" />
    </IconButton>
  );

  return (
    <>
      {short ? (
        <Tooltip
          title={short}
          describeChild
          open={tooltipOpen && !open}
          onOpen={() => setTooltipOpen(true)}
          onClose={() => setTooltipOpen(false)}
        >
          {trigger}
        </Tooltip>
      ) : (
        trigger
      )}
      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        // A long Detail scrolls inside the paper, so the paper must be keyboard-focusable. Modal
        // forces tabIndex -1 onto its child unless the transition already carries one, and the
        // transition hands it on to the paper.
        slotProps={{ transition: { tabIndex: 0 } }}
      >
        <Box sx={{ maxWidth: 440, p: 2, overflowWrap: 'break-word' }}>
          {scope ? <Chip label={scope} size="small" sx={{ mb: 1 }} /> : null}
          {short ? (
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {short}
            </Typography>
          ) : null}
          {body ? <Typography variant="body2">{body}</Typography> : null}
          {detail ? <Box sx={{ mt: short ? 1 : 0 }}>{detail}</Box> : null}
        </Box>
      </Popover>
    </>
  );
}
